import { BehaviorSubject } from 'rxjs';

type Token<T> = T & { __color_token__: true };

export type HEXColor = `#${string}`;
export type LABColor = [number, number, number];

/** @fixme we use hex values as tokens in order to avoid data migration.  */
type DefaultColorToken =
  | Token<'#fe7f21'>
  | Token<'#0346ff'>
  | Token<'#63d1f9'>
  | Token<'#f00eca'>
  | Token<'#d62728'>
  | Token<'#0cb9a5'>
  | Token<'#a88e71'>
  | Token<'#9b2f90'>
  | Token<'#ffd300'>
  | Token<'#089c08'>
  | Token<'#98df8a'>
  | Token<'#fea19d'>
  | Token<'#7b3f00'>
  | Token<'#f67ce8'>
  | Token<'#838803'>
  | Token<'#9467bd'>;

const DEFAULT_TOKEN_ALIASES = {
  DFT01: '#fe7f21' as Token<'#fe7f21'>,
  DFT02: '#0346ff' as Token<'#0346ff'>,
  DFT03: '#63d1f9' as Token<'#63d1f9'>,
  DFT04: '#f00eca' as Token<'#f00eca'>,
  DFT05: '#d62728' as Token<'#d62728'>,
  DFT06: '#0cb9a5' as Token<'#0cb9a5'>,
  DFT07: '#a88e71' as Token<'#a88e71'>,
  DFT08: '#9b2f90' as Token<'#9b2f90'>,
  DFT09: '#ffd300' as Token<'#ffd300'>,
  DFT0A: '#089c08' as Token<'#089c08'>,
  DFT0B: '#98df8a' as Token<'#98df8a'>,
  DFT0C: '#fea19d' as Token<'#fea19d'>,
  DFT0D: '#7b3f00' as Token<'#7b3f00'>,
  DFT0E: '#f67ce8' as Token<'#f67ce8'>,
  DFT0F: '#838803' as Token<'#838803'>,
  DFT10: '#9467bd' as Token<'#9467bd'>,
} as const;

type CVDColorToken =
  | Token<'#fe7f21'>
  | Token<'#0346ff'>
  | Token<'#63d1f9'>
  | Token<'#f00eca'>
  | Token<'#d62728'>
  | Token<'#0cb9a5'>
  | Token<'#a88e71'>
  | Token<'#9b2f90'>;

const CVD_TOKEN_ALIASES = {
  CVD01: '#fe7f21' as Token<'#fe7f21'>,
  CVD02: '#0346ff' as Token<'#0346ff'>,
  CVD03: '#63d1f9' as Token<'#63d1f9'>,
  CVD04: '#f00eca' as Token<'#f00eca'>,
  CVD05: '#d62728' as Token<'#d62728'>,
  CVD06: '#0cb9a5' as Token<'#0cb9a5'>,
  CVD07: '#a88e71' as Token<'#a88e71'>,
  CVD08: '#9b2f90' as Token<'#9b2f90'>,
} as const;

export type ColorTokensAlias = keyof typeof DEFAULT_TOKEN_ALIASES;

export type ColorToken = DefaultColorToken | CVDColorToken;

type CVDColorTuple = [CVDColorToken, HEXColor];
type DefaultColorTuple = [DefaultColorToken, HEXColor];

export type ColorTuple = [ColorToken, HEXColor];

export const DEFAULT_COLOR = '#8D9195' as const;
export const DEFAULT_PURPLE_COLOR = '#9b2f90' as const;

export const PALETTE_ALIASES = {
  BASE: 'base-palette',
  CVD: 'cvd-palette',
} as const;

export type PaletteAlias = (typeof PALETTE_ALIASES)[keyof typeof PALETTE_ALIASES];

/** neptune 16 colors palette */

export const BASE_COLOR_PALETTE: DefaultColorTuple[] = [
  [DEFAULT_TOKEN_ALIASES['DFT01'], '#fe7f21'],
  [DEFAULT_TOKEN_ALIASES['DFT02'], '#0346ff'],
  [DEFAULT_TOKEN_ALIASES['DFT03'], '#63d1f9'],
  [DEFAULT_TOKEN_ALIASES['DFT04'], '#eb2683'],
  [DEFAULT_TOKEN_ALIASES['DFT05'], '#d62728'],
  [DEFAULT_TOKEN_ALIASES['DFT06'], '#0cb9a5'],
  [DEFAULT_TOKEN_ALIASES['DFT07'], '#a88e71'],
  [DEFAULT_TOKEN_ALIASES['DFT08'], DEFAULT_PURPLE_COLOR],
  [DEFAULT_TOKEN_ALIASES['DFT09'], '#ffd300'],
  [DEFAULT_TOKEN_ALIASES['DFT0A'], '#089c08'],
  [DEFAULT_TOKEN_ALIASES['DFT0B'], '#98df8a'],
  [DEFAULT_TOKEN_ALIASES['DFT0C'], '#fea19d'],
  [DEFAULT_TOKEN_ALIASES['DFT0D'], '#7b3f00'],
  [DEFAULT_TOKEN_ALIASES['DFT0E'], '#f67ce8'],
  [DEFAULT_TOKEN_ALIASES['DFT0F'], '#838803'],
  [DEFAULT_TOKEN_ALIASES['DFT10'], '#9467bd'],
] as const;

const BASE_COLOR_CLUSTERS: Set<DefaultColorTuple>[] = [
  new Set([BASE_COLOR_PALETTE[7], BASE_COLOR_PALETTE[15]]),
  new Set([BASE_COLOR_PALETTE[3], BASE_COLOR_PALETTE[13]]),
  new Set([BASE_COLOR_PALETTE[1], BASE_COLOR_PALETTE[2]]),
  new Set([BASE_COLOR_PALETTE[4], BASE_COLOR_PALETTE[12]]),
  new Set([BASE_COLOR_PALETTE[9], BASE_COLOR_PALETTE[10], BASE_COLOR_PALETTE[14]]),
  new Set([BASE_COLOR_PALETTE[5]]),
  new Set([BASE_COLOR_PALETTE[11], BASE_COLOR_PALETTE[6]]),
  new Set([BASE_COLOR_PALETTE[0]]),
  new Set([BASE_COLOR_PALETTE[8]]),
] as const;

/** neptune 16 colors palette */

/** color vision deficiency(CVD) friendly 8 colors palette */

export const CVD_COLOR_PALETTE: CVDColorTuple[] = [
  [CVD_TOKEN_ALIASES['CVD01'], '#fe7f21'],
  [CVD_TOKEN_ALIASES['CVD02'], '#0346ff'],
  [CVD_TOKEN_ALIASES['CVD03'], '#63d1f9'],
  [CVD_TOKEN_ALIASES['CVD04'], '#eb2683'],
  [CVD_TOKEN_ALIASES['CVD05'], '#d62728'],
  [CVD_TOKEN_ALIASES['CVD06'], '#0cb9a5'],
  [CVD_TOKEN_ALIASES['CVD07'], '#a88e71'],
  [CVD_TOKEN_ALIASES['CVD08'], DEFAULT_PURPLE_COLOR],
] as const;

const CVD_COLOR_CLUSTERS: Set<CVDColorTuple>[] = [
  new Set([CVD_COLOR_PALETTE[0]]),
  new Set([CVD_COLOR_PALETTE[1], CVD_COLOR_PALETTE[7]]),
  new Set([CVD_COLOR_PALETTE[2]]),
  new Set([CVD_COLOR_PALETTE[3]]),
  new Set([CVD_COLOR_PALETTE[4]]),
  new Set([CVD_COLOR_PALETTE[5], CVD_COLOR_PALETTE[6]]),
] as const;

/** color vision deficiency(CVD) friendly 8 colors palette */

const COLOR_PALETTE_SUBJECT = new BehaviorSubject<ColorTuple[]>(BASE_COLOR_PALETTE);
const COLOR_CLUSTERS_SUBJECT = new BehaviorSubject<Set<ColorTuple>[]>(BASE_COLOR_CLUSTERS);

export let COLOR_PALETTE: ColorTuple[] = BASE_COLOR_PALETTE;
export let COLOR_CLUSTERS: Set<ColorTuple>[] = BASE_COLOR_CLUSTERS;
export const COLOR_MAPPING = new Map<ColorToken, HEXColor>([
  ...BASE_COLOR_PALETTE,
  ...CVD_COLOR_PALETTE,
]);

export let COLOR_MAP = BASE_COLOR_PALETTE.reduce<Map<ColorToken, ColorTuple>>(
  (acc, color) => (acc.set(color[0], color), acc),
  new Map<ColorToken, ColorTuple>(),
);

export function setColorPalette(palette: PaletteAlias) {
  if (palette === PALETTE_ALIASES.CVD) {
    update(CVD_COLOR_PALETTE, CVD_COLOR_CLUSTERS);
  } else {
    update(BASE_COLOR_PALETTE, BASE_COLOR_CLUSTERS);
  }
}

export function isColorToken(value: any): value is ColorToken {
  return COLOR_MAPPING.has(value);
}

type ReadColorInput = ColorToken | HEXColor | string | undefined;
type ReadColorResult<T extends ReadColorInput> = T extends ColorToken ? HEXColor : T;

export function readColor<T extends ReadColorInput>(value: T): ReadColorResult<T> {
  if (isColorToken(value)) {
    return COLOR_MAPPING.get(value) as ReadColorResult<T>;
  }

  return value as ReadColorResult<T>;
}

function update(palette: ColorTuple[], clusters: Set<ColorTuple>[]) {
  COLOR_PALETTE_SUBJECT.next(palette);
  COLOR_CLUSTERS_SUBJECT.next(clusters);

  COLOR_PALETTE = palette;
  COLOR_CLUSTERS = clusters;

  COLOR_MAP = palette.reduce<Map<ColorToken, ColorTuple>>(
    (acc, color) => (acc.set(color[0], color), acc),
    new Map<ColorToken, ColorTuple>(),
  );
}

/**
 * Colors carefully selected from the above palette. Do not change without talking to UX team.
 */
export const COLOR_PICKER_PRESET_COLORS = [
  '#fe7f21',
  '#0346ff',
  '#63d1f9',
  '#f00eca',
  '#d62728',
  '#0cb9a5',
  '#a88e71',
  '#9b2f90',
  '#ffd300',
  '#089c08',
  '#98df8a',
  '#fea19d',
  '#7b3f00',
  '#f67ce8',
  '#838803',
  '#9467bd',
];
